/**
 * Чип «Замечания» во вкладке «Юниверситет» (16.10.2026, владелец выбрал: «оператор видит свои и их статус, ОО и EO —
 * все и отмечают „Исправлено“»). Сверху — как оставить замечание (зажать любой элемент). «Мои / Все» — второе только
 * у ОО/EO (сервер решает сам, canAll). Действия: ОО/EO — «Исправлено» / «Вернуть в новые» и «Убрать» любое;
 * автор — «Убрать» своё, пока его не разобрали. Убрать — в два касания («точно?»), без системного confirm (A3).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { orenStamp } from "../../../../hooks/payoutsApi";
import { fetchFeedback, removeFeedback, setFeedbackStatus, type FbRow } from "./fbApi";
import u from "../uni.module.css";
import css from "./feedback.module.css";

type Scope = "mine" | "all";
type Filter = "new" | "done" | "any";
const FILTERS: [Filter, string][] = [["new", "Новые"], ["done", "Исправлено"], ["any", "Все"]];

export function FeedbackList({ canAllHint }: { canAllHint: boolean }) {
  const [scope, setScope] = useState<Scope>(canAllHint ? "all" : "mine");
  const [filter, setFilter] = useState<Filter>("any");
  const [rows, setRows] = useState<FbRow[] | null>(null);
  const [canAll, setCanAll] = useState(canAllHint);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [ask, setAsk] = useState<number | null>(null);
  const busy = useRef(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setErr(null);
    fetchFeedback(scope).then(
      (r) => { if (!alive) return; setRows(r.rows); setCanAll(r.canAll); if (!r.canAll && scope === "all") setScope("mine"); },
      (e: unknown) => { if (alive) setErr(e instanceof Error ? e.message : "нет связи"); },
    );
    return () => { alive = false; };
  }, [scope, tick]);

  const act = useCallback(async (id: number, fn: () => Promise<unknown>) => {
    if (busy.current) return;
    busy.current = true; setBusyId(id); setErr(null);
    try { await fn(); setAsk(null); setTick((n) => n + 1); }
    catch (e) { setErr(e instanceof Error ? e.message : "Не получилось — попробуйте ещё раз"); }
    finally { busy.current = false; setBusyId(null); }
  }, []);

  const shown = (rows ?? []).filter((r) => filter === "any" || r.status === filter);
  const count = (f: Filter) => (rows ?? []).filter((r) => f === "any" || r.status === f).length;
  return (
    <div className={u.root} data-fb-skip="">
      <p className={css.how}>
        Нашли ошибку? Зажмите на полсекунды любое место Юниверситета — вопрос, вариант ответа, картинку, комнату
        на карте — и напишите, что исправить.
      </p>
      <div className={css.bar}>
        {canAll ? (
          <div className={css.seg} role="group" aria-label="Чьи замечания">
            <button type="button" className={scope === "mine" ? css.segOn : ""} aria-pressed={scope === "mine"} onClick={() => setScope("mine")}>Мои</button>
            <button type="button" className={scope === "all" ? css.segOn : ""} aria-pressed={scope === "all"} onClick={() => setScope("all")}>Все</button>
          </div>
        ) : null}
        <div className={css.seg} role="group" aria-label="Статус">
          {FILTERS.map(([f, label]) => (
            <button key={f} type="button" className={filter === f ? css.segOn : ""} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {label} <b>{rows ? count(f) : "…"}</b>
            </button>
          ))}
        </div>
      </div>
      {err ? <div className={css.err} role="alert">{err} <button type="button" className={css.link} onClick={() => setTick((n) => n + 1)}>Повторить</button></div> : null}
      {!rows ? (err ? null : <div className={u.state}><p>Загрузка…</p></div>)
        : !shown.length ? <div className={u.state}><p>{rows.length ? "Здесь пусто — смените фильтр" : "Замечаний пока нет"}</p></div>
          : (
            <ul className={css.list}>
              {shown.map((r) => (
                <li key={r.id} className={`${css.item} ${r.status === "done" ? css.done : ""}`}>
                  <div className={css.place}>{r.place || r.anchor}{r.part ? ` · ${r.part}` : ""}</div>
                  {r.quote ? <blockquote className={css.quote}>{r.quote}</blockquote> : null}
                  {r.media ? <div className={css.meta}>файл: {r.media}</div> : null}
                  <p className={css.comment}>{r.comment}</p>
                  <div className={css.meta}>
                    <span className={`${css.pill} ${r.status === "done" ? css.pillDone : css.pillNew}`}>{r.status === "done" ? "исправлено" : "новое"}</span>
                    {scope === "all" ? <span>{r.staff_name || r.staff_id}</span> : null}
                    <span>{orenStamp(r.created_at)}</span>
                    {r.pack_version != null ? <span>пакет v{r.pack_version}</span> : null}
                  </div>
                  {r.status === "done" ? (
                    <div className={css.meta}>Исправлено · {[r.resolved_by_name, orenStamp(r.resolved_at), r.resolve_note].filter(Boolean).join(" · ")}</div>
                  ) : null}
                  <div className={css.acts}>
                    {canAll ? (
                      <button type="button" className={css.act} disabled={busyId === r.id}
                        onClick={() => void act(r.id, () => setFeedbackStatus(r.id, r.status === "done" ? "new" : "done"))}>
                        {r.status === "done" ? "Вернуть в новые" : "Исправлено"}
                      </button>
                    ) : null}
                    {canAll || (r.mine && r.status === "new") ? (
                      ask === r.id ? (
                        <span className={css.ask}>Убрать замечание?
                          <button type="button" className={css.yes} disabled={busyId === r.id} onClick={() => void act(r.id, () => removeFeedback(r.id))}>{busyId === r.id ? "…" : "да, убрать"}</button>
                          <button type="button" className={css.act} disabled={busyId === r.id} onClick={() => setAsk(null)}>нет</button>
                        </span>
                      ) : <button type="button" className={css.act} onClick={() => setAsk(r.id)}>Убрать</button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
    </div>
  );
}
